import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { X, Save, UserPlus, Info } from 'lucide-react';
import axios from 'axios';

const ALL_LOAN_TYPES = [
  { id: 'sba_loan', label: 'SBA Loan' },
  { id: 'term_loan_10yr', label: '10 Year Term Loan' },
  { id: 'term_loan_3_5yr', label: 'EZ Term Loan 3-5 Yr' },
  { id: 'term_loc_hybrid', label: 'Term Loan / LOC Hybrid' },
  { id: 'line_of_credit', label: 'Line of Credit' },
  { id: 'equipment_financing', label: 'Equipment Financing' },
  { id: 'merchant_processing', label: 'Merchant Processing' },
  { id: 'jumbo_term_loan', label: 'Jumbo Term Loan' },
  { id: 'transportation_factoring', label: 'Transportation Factoring' },
  { id: 'real_estate_financing', label: 'Real Estate Financing' },
  { id: 'debt_consulting', label: 'Debt Consolidation' },
  { id: 'foreclosure_bailout', label: 'Foreclosure Bailout' },
  { id: 'family_office', label: 'Family Office Programs' },
];

const CreateUserModal = ({ onClose, onSuccess, allowedRoles = null, creatorRole = 'admin' }) => {
  // `allowedRoles`: optional whitelist of role values the modal may offer.
  //   Defaults to all four when null/undefined (admin behaviour).
  // `creatorRole`: used to tailor copy + indicate whether approval is needed.
  const requiresApproval = creatorRole !== 'admin';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    roles: [],
    allowed_loan_types: ['sba_loan', 'term_loan_10yr', 'term_loan_3_5yr', 'term_loc_hybrid', 'line_of_credit', 'equipment_financing', 'merchant_processing', 'jumbo_term_loan', 'transportation_factoring', 'real_estate_financing', 'debt_consulting', 'foreclosure_bailout', 'family_office']
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const handleLoanTypeToggle = (typeId) => {
    setFormData(prev => ({
      ...prev,
      allowed_loan_types: prev.allowed_loan_types.includes(typeId)
        ? prev.allowed_loan_types.filter(t => t !== typeId)
        : [...prev.allowed_loan_types, typeId]
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      alert('Please enter a name');
      return false;
    }
    if (!formData.email.trim()) {
      alert('Please enter an email address');
      return false;
    }
    if (!formData.password) {
      alert('Please enter a password');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match');
      return false;
    }
    if (formData.password.length < 6) {
      alert('Password must be at least 6 characters long');
      return false;
    }
    if (formData.roles.length === 0) {
      alert('Please select at least one role');
      return false;
    }
    return true;
  };

  const handleCreateUser = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');

      await axios.post(
        `${backendUrl}/api/users/create`,
        {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          company_name: formData.company_name,
          roles: formData.roles,
          allowed_loan_types: (formData.roles.includes('broker') || formData.roles.includes('underwriter')) ? formData.allowed_loan_types : []
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      onSuccess();
      onClose();
      alert(requiresApproval ? '✅ User created — awaiting admin approval before they can log in.' : '✅ User created successfully');
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMsg = error.response?.data?.detail || 'Please try again.';
      alert(`❌ Error creating user: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const availableRoles = [
    { 
      value: 'admin', 
      name: 'Administrator',
      description: 'Full system access, user management, application oversight'
    },
    { 
      value: 'broker', 
      name: 'Broker',
      description: 'Create applications, manage clients, access professional tools'
    },
    { 
      value: 'underwriter', 
      name: 'Underwriter',
      description: 'Review applications, update status, access professional tools'
    },
    {
      value: 'jr_underwriter',
      name: 'Jr Underwriter',
      description: 'Review only applications assigned to your underwriting team'
    },
    { 
      value: 'borrower', 
      name: 'Borrower',
      description: 'Submit applications, view own applications, upload documents'
    }
  ].filter(r => !allowedRoles || allowedRoles.includes(r.value));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 flex items-center">
              <UserPlus className="h-5 w-5 mr-2" />
              Create New User
            </h2>
            <p className="text-slate-600">Add a new user to the system</p>
          </div>
          <button onClick={onClose} disabled={loading} className="h-9 w-9 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-100 text-slate-600 hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-all duration-200" data-testid="close-create-user-modal">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {requiresApproval
                ? 'New users you create will appear in the Admin “Pending Approvals” queue and cannot log in until an administrator approves them.'
                : 'Admin-created users are automatically approved and can login immediately.'}
            </AlertDescription>
          </Alert>

          {/* User Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">User Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={loading}
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={loading}
                    placeholder="user@example.com"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="company_name">Company Name (Optional)</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  disabled={loading}
                  placeholder="Company or organization name"
                />
              </div>
            </CardContent>
          </Card>

          {/* Password */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Password</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    disabled={loading}
                    placeholder="Enter password (min 6 characters)"
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    disabled={loading}
                    placeholder="Confirm password"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Roles Management */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">User Roles *</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Select the roles this user should have:</p>
                <div className="space-y-3">
                  {availableRoles.map((role) => (
                    <div
                      key={role.value}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        formData.roles.includes(role.value)
                          ? 'bg-slate-100 border-slate-400'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleRoleToggle(role.value)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <span className="font-medium">{role.name}</span>
                          {formData.roles.includes(role.value) && (
                            <Badge className="bg-teal-600 text-white">Selected</Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-slate-600">{role.description}</p>
                    </div>
                  ))}
                </div>
                <div className="text-sm text-slate-500 mt-3">
                  Selected roles: {formData.roles.length > 0 ? formData.roles.join(', ') : 'None'}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Allowed Loan Products - shown when broker or underwriter role is selected */}
          {(formData.roles.includes('broker') || formData.roles.includes('underwriter')) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Allowed Loan Products</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-3">
                  Select the loan products this {formData.roles.includes('underwriter') ? 'underwriter can review' : 'broker can submit applications for'}:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ALL_LOAN_TYPES.map((type) => (
                    <div
                      key={type.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.allowed_loan_types.includes(type.id)
                          ? 'bg-teal-50 border-teal-400'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleLoanTypeToggle(type.id)}
                      data-testid={`toggle-loan-${type.id}`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        formData.allowed_loan_types.includes(type.id) ? 'bg-teal-600 border-teal-600' : 'border-slate-300'
                      }`}>
                        {formData.allowed_loan_types.includes(type.id) && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-medium">{type.label}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  {formData.allowed_loan_types.filter(id => ALL_LOAN_TYPES.some(t => t.id === id)).length} of {ALL_LOAN_TYPES.length} products enabled
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateUser} 
            disabled={loading}
            className="bg-teal-600 hover:bg-teal-700"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating User...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create User
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateUserModal;